import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

interface AuthUser {
  id: string;
  email: string;
  role: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function shouldVerifyAuthOnPath(pathname: string) {
  return pathname === "/auth" || pathname.startsWith("/admin");
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(() => shouldVerifyAuthOnPath(location.pathname));

  const verifyToken = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const response = await fetch("/.netlify/functions/admin-auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "verify" }),
      });
      if (response.ok) {
        const data = await response.json();
        return data.user ?? null;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let isCurrent = true;

    if (!shouldVerifyAuthOnPath(location.pathname)) {
      setUser(null);
      setIsLoading(false);
      return () => {
        isCurrent = false;
      };
    }

    setIsLoading(true);
    verifyToken().then((authUser) => {
      if (!isCurrent) return;
      setUser(authUser);
      setIsLoading(false);
    });

    return () => {
      isCurrent = false;
    };
  }, [location.pathname, verifyToken]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const response = await fetch("/.netlify/functions/admin-auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "login", email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        return { success: true };
      }

      return { success: false, error: data.error || "Invalid credentials" };
    } catch {
      return { success: false, error: "Network error. Please try again." };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await fetch("/.netlify/functions/admin-auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "logout" }),
      });
    } finally {
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isAdmin: user?.role === "admin",
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
